import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import {
  Viewer,
  TemplateViewers,
  User,
  Team,
  CRM,
  DocuSealTemplate,
} from './docusealHelpers';

interface AddViewerDialogProps {
  open: boolean;
  templateId: string;
  onClose: () => void;
  availableUsers: User[];
  availableTeams: Team[];
  availableCRMs: CRM[];
  templates: DocuSealTemplate[];
  templateViewers: TemplateViewers[];
  onUpdateViewers: (templateId: string, viewers: Viewer[]) => void;
  onUpdateCRMs: (templateId: string, crmIds: string[]) => void;
}

interface AutocompleteOption {
  id: string;
  name: string;
  type: 'user' | 'team' | 'everyone';
  category: string;
  email?: string;
}

const AddViewerDialog: React.FC<AddViewerDialogProps> = ({
  open,
  templateId,
  onClose,
  availableUsers,
  availableTeams,
  availableCRMs,
  templates,
  templateViewers,
  onUpdateViewers,
  onUpdateCRMs,
}) => {
  const [selectedItems, setSelectedItems] = useState<AutocompleteOption[]>([]);
  const [selectedCRMs, setSelectedCRMs] = useState<CRM[]>([]);

  // Get current template's viewers and CRMs
  const currentTemplate = templates.find((t) => t.id === templateId);
  const currentTemplateData = templateViewers.find(
    (tp) => tp.templateId === templateId
  );
  const currentViewers = currentTemplateData?.viewers || [];
  const currentCRMs = currentTemplateData?.crms || [];

  // Create everyone option for display
  const everyoneOption: AutocompleteOption = {
    id: 'everyone',
    name: 'Everyone',
    type: 'everyone',
    category: 'Access Level',
  };

  // Create combined options array with Everyone, Users, and Teams
  const allOptions: AutocompleteOption[] = [
    // Everyone option
    everyoneOption,
    // Users
    ...availableUsers.map((user) => ({
      ...user,
      id: `user-${user.id}`, // Ensure unique ID
      type: 'user' as const,
      category: 'Users',
    })),
    // Teams
    ...availableTeams.map((team) => ({
      ...team,
      id: `team-${team.id}`, // Ensure unique ID
      type: 'team' as const,
      category: 'Teams',
    })),
  ];

  // Populate selectedItems and selectedCRMs when dialog opens
  useEffect(() => {
    if (open) {
      // Populate viewers
      if (currentViewers.length === 0) {
        // Empty viewers array = everyone access, so show everyone in selection
        setSelectedItems([everyoneOption]);
      } else {
        // Convert existing viewers back to autocomplete format
        // Find the matching items from allOptions to ensure exact object reference
        const existingItems: AutocompleteOption[] = [];

        currentViewers.forEach((viewer) => {
          if (viewer.type === 'user') {
            const matchingOption = allOptions.find(
              (opt) => opt.type === 'user' && opt.id === `user-${viewer.userID}`
            );
            if (matchingOption) {
              existingItems.push(matchingOption);
            }
          } else if (viewer.type === 'team') {
            const matchingOption = allOptions.find(
              (opt) => opt.type === 'team' && opt.id === `team-${viewer.teamID}`
            );
            if (matchingOption) {
              existingItems.push(matchingOption);
            }
          }
        });

        setSelectedItems(existingItems);
      }

      // Populate CRMs
      if (currentCRMs.length === 0) {
        // Empty CRMs array = all CRMs
        setSelectedCRMs([]);
      } else {
        // Find matching CRMs from availableCRMs
        const existingCRMs = availableCRMs.filter((crm) =>
          currentCRMs.includes(crm.id)
        );
        setSelectedCRMs(existingCRMs);
      }
    } else {
      // Clear selections when dialog closes
      setSelectedItems([]);
      setSelectedCRMs([]);
    }
  }, [
    open,
    templateId,
    currentViewers.length,
    currentCRMs.length,
    availableUsers,
    availableTeams,
    availableCRMs,
  ]);

  const handleAdd = () => {
    // Use the same logic as the visual display to determine what's actually selected
    const displayValue =
      selectedItems.length > 0 ? selectedItems : [everyoneOption];
    const hasEveryone = displayValue.some((item) => item.type === 'everyone');

    if (hasEveryone) {
      // Empty array represents everyone access
      onUpdateViewers(templateId, []);
    } else {
      const newViewers: Viewer[] = [];
      selectedItems.forEach((item) => {
        if (item.type === 'everyone') return;

        const originalId = item.id.replace(/^(user-|team-)/, '');

        const viewer: Viewer = {
          id: `${templateId}-${item.type}-${originalId}`,
          type: item.type,
          name: item.name,
          ...(item.type === 'user'
            ? { userID: originalId }
            : { teamID: originalId }),
        };
        newViewers.push(viewer);
      });

      onUpdateViewers(templateId, newViewers);
    }

    // Update CRM selections (empty array means all CRMs)
    const crmIds = selectedCRMs.map((crm) => crm.id);
    onUpdateCRMs(templateId, crmIds);

    onClose();
  };

  const handleClose = () => {
    setSelectedItems([]);
    setSelectedCRMs([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          height: 'auto',
          position: 'relative',
          overflow: 'visible',
          marginTop: '5vh',
          marginBottom: '5vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
        }}
      >
        Manage Viewers for &quot;{currentTemplate?.name}&quot;
      </DialogTitle>
      <DialogContent
        sx={{
          pt: 3,
          overflow: 'visible',
          minHeight: '450px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box sx={{ mb: 3, position: 'relative', zIndex: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select who can view this template. Choose specific users and teams,
            or select &quot;Everyone&quot; to make it accessible to all
            organization members.
          </Typography>

          <Box sx={{ position: 'relative' }}>
            <Autocomplete
              multiple
              value={
                selectedItems.length > 0 ? selectedItems : [everyoneOption]
              }
              options={allOptions}
              groupBy={(option) => option.category}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              disableClearable
              disablePortal={true}
              ListboxProps={{
                style: {
                  maxHeight: '300px',
                  overflow: 'auto',
                },
              }}
              componentsProps={{
                popper: {
                  sx: {
                    zIndex: 1300, // Below modal backdrop but above modal content
                  },
                },
              }}
              onChange={(_, newValue) => {
                // Remove duplicates based on ID
                const uniqueItems = newValue.filter(
                  (item, index, arr) =>
                    arr.findIndex((other) => other.id === item.id) === index
                );

                const currentValue = selectedItems;
                const newlySelected = uniqueItems.find(
                  (item) =>
                    !currentValue.some((current) => current.id === item.id)
                );

                const everyoneJustSelected = newlySelected?.id === 'everyone';
                const nonEveryoneSelected =
                  newlySelected && newlySelected.id !== 'everyone';

                if (everyoneJustSelected) {
                  // If everyone was just selected, only keep everyone
                  setSelectedItems([everyoneOption]);
                } else if (nonEveryoneSelected) {
                  // If a specific user/team was selected, remove everyone
                  setSelectedItems(
                    uniqueItems.filter((item) => item.id !== 'everyone')
                  );
                } else {
                  // Handle removals
                  const hasEveryone = uniqueItems.some(
                    (item) => item.id === 'everyone'
                  );
                  if (hasEveryone) {
                    setSelectedItems([everyoneOption]);
                  } else {
                    setSelectedItems(uniqueItems);
                  }
                }
              }}
              renderOption={(props, option) => {
                const duplicateNames = allOptions.filter(
                  (opt) => opt.name === option.name && opt.id !== option.id
                );
                const hasDuplicateName = duplicateNames.length > 0;

                return (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1">
                        {option.name}
                        {hasDuplicateName && option.type !== 'user' && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            (ID: {option.id.replace(/^(user-|team-)/, '')})
                          </Typography>
                        )}
                      </Typography>
                      {option.type === 'user' && option.email && (
                        <Typography variant="body2" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    color={
                      option.type === 'everyone'
                        ? 'primary'
                        : option.type === 'user'
                        ? 'info'
                        : 'success'
                    }
                    variant={option.type === 'everyone' ? 'filled' : 'outlined'}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Viewers"
                  placeholder="Choose who can view this template..."
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which CRMs can access this template. Leave empty to make it
            accessible to all CRMs.
          </Typography>

          <Box sx={{ position: 'relative' }}>
            <Autocomplete
              multiple
              value={selectedCRMs}
              options={availableCRMs}
              getOptionLabel={(option) => option.title}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              disablePortal={true}
              ListboxProps={{
                style: {
                  maxHeight: '250px',
                  overflow: 'auto',
                },
              }}
              componentsProps={{
                popper: {
                  sx: {
                    zIndex: 1300,
                  },
                },
              }}
              onChange={(_, newValue) => {
                setSelectedCRMs(newValue);
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.title}
                    color="warning"
                    variant="outlined"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select CRMs (Optional)"
                  placeholder={
                    selectedCRMs.length === 0
                      ? 'All CRMs (default)'
                      : 'Add more CRMs...'
                  }
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          color="primary"
          sx={{ minWidth: 120 }}
        >
          {(() => {
            const displayValue =
              selectedItems.length > 0 ? selectedItems : [everyoneOption];
            const hasEveryone = displayValue.some(
              (item) => item.type === 'everyone'
            );

            if (hasEveryone) {
              return 'Set Everyone Access';
            } else if (selectedItems.length > 0) {
              return `Add Viewers (${selectedItems.length})`;
            } else {
              return 'Set Everyone Access';
            }
          })()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddViewerDialog;
